/**
 * @jsxRuntime classic
 * @jsx jsx
 */
/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import { useEffect } from 'react';

import { FormattedMessage, useIntl } from 'react-intl-next';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import Button from '@atlaskit/button/new';
import { cssMap, jsx } from '@atlaskit/css';
import Heading from '@atlaskit/heading';
import Image from '@atlaskit/image';
import { Box } from '@atlaskit/primitives/box';
import Text from '@atlaskit/primitives/text';
import { token } from '@atlaskit/tokens';

import TimeoutImage from '../../assets/warning.png';
import { ScreenLayout } from '../../components/screen-layout';
import { useModalContext } from '../../contexts/modal-context';

import { messages } from './messages';

const styles = cssMap({
	modalContent: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		borderRadius: token('radius.large'),
		marginTop: token('space.600'),
		marginLeft: token('space.600'),
		marginRight: token('space.600'),
		marginBottom: token('space.600'),
		textAlign: 'center',
		flex: 1,
	},
	iconWrapper: {
		marginBottom: token('space.200'),
		marginTop: token('space.400'),
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
	},
	heading: {
		marginBottom: token('space.200'),
	},
	description: {
		marginBottom: token('space.600'),
		maxWidth: '400px',
	},
});

type TimeoutScreenProps = {
	onClose: () => void;
};

export const TimeoutScreen: (props: TimeoutScreenProps) => JSX.Element = ({
	onClose,
}: TimeoutScreenProps): JSX.Element => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const intl = useIntl();
	const { sourceComponent, sourceContext } = useModalContext();

	useEffect(() => {
		createAnalyticsEvent({
			eventType: 'screen',
			name: 'twcXflowModalTimeoutScreen',
			attributes: {
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');
	}, [createAnalyticsEvent, sourceComponent, sourceContext]);

	const handleCloseClick = () => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'clicked',
			actionSubject: 'button',
			actionSubjectId: 'twcXflowModalTimeoutScreenClose',
			attributes: {
				sourceComponent,
				sourceContext,
			},
		}).fire('growth');

		onClose();
	};

	return (
		<ScreenLayout testId="timeout-screen">
			<ScreenLayout.Content>
				<Box as="div" xcss={styles.modalContent}>
					<Box xcss={styles.iconWrapper}>
						<Image
							src={TimeoutImage}
							width={150}
							height={150}
							testId="timeout-icon"
							alt={intl.formatMessage(messages.timeoutIconAlt)}
						/>
					</Box>

					<Box xcss={styles.heading}>
						<Heading size="medium">
							<FormattedMessage {...messages.heading} />
						</Heading>
					</Box>

					<Box xcss={styles.description}>
						<Text size="medium" weight="regular" color="color.text.subtlest">
							<FormattedMessage {...messages.description} />
						</Text>
					</Box>

					<Button appearance="primary" onClick={handleCloseClick}>
						<FormattedMessage {...messages.closeButton} />
					</Button>
				</Box>
			</ScreenLayout.Content>
		</ScreenLayout>
	);
};


